import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';
import { config } from './env';

// Export the isSupabaseConfigured function from the env module
export const isSupabaseConfigured = () => config.supabase.url && config.supabase.anonKey;

// Lazy-initialized Supabase client
let supabase: ReturnType<typeof createClient<Database>> | null = null;

const ensureClient = () => {
  if (!supabase) {
    try {
      const storagePref = (import.meta as any).env?.VITE_AUTH_STORAGE || 'local';
      const storageBackend = storagePref === 'session' ? window.sessionStorage : window.localStorage;
      supabase = createClient<Database>(config.supabase.url, config.supabase.anonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
          storage: {
            getItem: (key: string) => {
              try {
                return storageBackend.getItem(key);
              } catch {
                return null;
              }
            },
            setItem: (key: string, value: string) => {
              try {
                storageBackend.setItem(key, value);
              } catch {
                // Handle cases where localStorage is not available
              }
            },
            removeItem: (key: string) => {
              try {
                storageBackend.removeItem(key);
              } catch {
                // Handle cases where localStorage is not available
              }
            },
          },
        },
        global: {
          headers: {
            'X-Client-Info': 'supabase-js-web',
            'X-Requested-With': 'XMLHttpRequest',
          },
        },
      });
    } catch (error) {
      console.error('Failed to initialize Supabase client:', error);
      supabase = null;
    }
  }

  return supabase;
};

export { supabase };

export const getSupabaseClient = () => {
  const client = ensureClient();
  if (!client) {
    throw new Error('Supabase is not configured. Please check your environment variables.');
  }
  return client;
};
